import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import api from '../../../services/api';
import { COMPONENT_QUERY } from '../../../constants/api';
import {
  getPayload,
  setErrorsFunction,
  transformHeaders,
  transformData,
  transformMonthlyData,
  verifyRowData,
  getChannelAndAudienceHeaders,
  isStoreValid,
  formateGrandTotalColumnsData,
  appendChannelFilter,
  stableObject,
} from '../../../constants/utils';
import { previousWeekExtractor } from '../../../data/branchPerformanceData';
import {
  ACTUAL_PLAN_WEEKLY_SPEND,
  ACTUAL_PLAN_WEEKLY_HEADERS,
  ACTUAL_PLAN_MONTHLY_SPEND,
  ACTUAL_PLAN_WEEKLY_SPEND_WITHOUT_LOCAL,
  ACTUAL_PLAN_MONTHLY_SPEND_WITHOUT_LOCAL,
  ACTUAL_PLAN_WEEKLY_HEADERS_WITHOUT_LOCAL,
  ACTUAL_PLAN_DOWNLOAD_FILE_MONTHLY,
  ACTUAL_PLAN_DOWNLOAD_FILE_WEEKLY,
  ACTUAL_PLAN_DOWNLOAD_FILE_WEEKLY_LOCAL_SPEND,
  ACTUAL_PLAN_DOWNLOAD_FILE_MONTHLY_LOCAL_SPEND,
} from '../constant';
import selectors from '../../../modules/selectors/datePickerSelectors';
import { actualSpendDownloadComponent } from '../services/actualSpendServices';

const useActualSpendData = () => {
  const { channelsListForActualSpend, userStores, userData } = useSelector((state) => state.auth);
  const { transformedDataWithDate } = useSelector(selectors?.datePickerCombinedData);
  // Track initial mount and data loading
  const isFirstMount = useRef(true);
  const dataFetchedRef = useRef({
    weekly: false,
    headers: false,
    monthly: false,
  });

  // Add ref to track if we've already fetched monthly data for current filters
  const viewSwitchTracker = useRef({
    weekly: { fetched: false },
    monthly: { fetched: false },
  });

  // Track the previous view to detect view changes
  const previousViewRef = useRef(null);

  // Track if filters have been changed since last API call
  const filtersChangedSinceLastFetchRef = useRef({
    weekly: false,
    monthly: false,
  });

  // Initialize loading state to true by default
  const [initialLoading, setInitialLoading] = useState(true);
  const dataFetchInitiatedRef = useRef(false);

  // Data state
  const [weeklyTableHeaders, setWeeklyTableHeaders] = useState([]);
  const [weeklyRowData, setWeeklyRowData] = useState([]);
  const [monthlyTableHeaders, setMonthlyTableHeaders] = useState([]);
  const [monthlyRowData, setMonthlyRowData] = useState([]);
  const [errors, setErrors] = useState({
    weekly_data_error: null,
    headers_error: null,
    monthly_data_error: null,
  });

  // Filter state
  const [actualSpendView, setActualSpendView] = useState('Weekly');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedYear, setSelectedYear] = useState(userData?.data?.current_year);
  const [spendToggleState, setSpendToggleState] = useState(false);
  const [showSpendToggle, setShowSpendToggle] = useState(false);
  const resetDropdown = false;
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [hasActualData, setHasActualData] = useState(true);

  // Channel selection state (moved from component)
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [modifiedRowData, setModifiedRowData] = useState([]);
  const [modifiedMonthlyRowData, setModifiedMonthlyRowData] = useState([]);
  const [noDataForSelectedChannel, setNoDataForSelectedChannel] = useState(false);
  const [showNoChannelSelected, setShowNoChannelSelected] = useState(false);
  const [noDataForSelectedBranch, setNoDataForSelectedBranch] = useState(false);
  const initRef = useRef(false);

  // Track filter changes to avoid duplicate API calls
  const filterRef = useRef({ store: null, year: null, spendToggle: false });

  // Filter data - stabilized
  const defaultChannelList = useMemo(
    () => channelsListForActualSpend?.filter((channel) => channel?.label && channel?.value) || [],
    [stableObject(channelsListForActualSpend)]
  );
  const [updatedChannelListForActualSpend] = useState(defaultChannelList);
  // Store options - stabilized
  const options = useMemo(() => {
    if (!userStores?.length) return [];
    const stores = userStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter((store) => store.value === 1);
    if (GaleMarkets.length) {
      return [
        GaleMarkets[0],
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    } else {
      return [
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
  }, [stableObject(userStores)]);

  // Set default store on first render
  useEffect(() => {
    if (options.length && !selectedStore) {
      setSelectedStore(options[0]?.value);
    }
  }, [options, selectedStore]);

  // Year list for dropdown - stabilized
  const currentYear = userData?.data?.current_year;
  const yearsList = useMemo(
    () =>
      transformedDataWithDate?.length > 0 && currentYear
        ? Array.from({ length: transformedDataWithDate?.length - 4 }, (_, i) => ({
            label: `${currentYear - i}`,
            value: currentYear - i,
          }))
        : [],
    [transformedDataWithDate?.length, currentYear]
  );

  // Initialize channel filters once
  useEffect(() => {
    if (updatedChannelListForActualSpend?.length && !initRef.current) {
      initRef.current = true;
      setSelectedChannels(updatedChannelListForActualSpend.map((item) => item.value));
    }
  }, [updatedChannelListForActualSpend]);

  // Update spend toggle visibility based on store
  useEffect(() => {
    if (selectedStore) {
      setShowSpendToggle(isStoreValid(selectedStore, options));
    }
  }, [selectedStore, options]);

  // Query payloads - stabilized to prevent unnecessary API calls
  const weeklyPayload = useMemo(() => {
    if (!selectedStore || !transformedDataWithDate?.length) return null;

    const component = spendToggleState
      ? ACTUAL_PLAN_WEEKLY_SPEND_WITHOUT_LOCAL
      : ACTUAL_PLAN_WEEKLY_SPEND;
    return {
      component,
      payload: getPayload(
        userData?.data?.is_gale_admin,
        component,
        selectedStore,
        selectedYear,
        false,
        transformedDataWithDate,
        previousWeekExtractor
      ),
    };
  }, [
    selectedStore,
    selectedYear,
    spendToggleState,
    transformedDataWithDate?.length,
    userData?.data?.is_gale_admin,
  ]);

  const headersPayload = useMemo(() => {
    if (!selectedStore || !transformedDataWithDate?.length) return null;
    const component = spendToggleState
      ? ACTUAL_PLAN_WEEKLY_HEADERS_WITHOUT_LOCAL
      : ACTUAL_PLAN_WEEKLY_HEADERS;
    return {
      component,
      payload: getPayload(
        true,
        component,
        selectedStore,
        selectedYear,
        false,
        transformedDataWithDate,
        previousWeekExtractor
      ),
    };
  }, [selectedStore, selectedYear, spendToggleState, transformedDataWithDate?.length]);

  const monthlyPayload = useMemo(() => {
    if (!selectedStore || !transformedDataWithDate?.length) return null;

    const component = spendToggleState
      ? ACTUAL_PLAN_MONTHLY_SPEND_WITHOUT_LOCAL
      : ACTUAL_PLAN_MONTHLY_SPEND;
    return {
      component,
      payload: getPayload(
        userData?.data?.is_gale_admin,
        component,
        selectedStore,
        selectedYear,
        false,
        transformedDataWithDate,
        previousWeekExtractor
      ),
    };
  }, [
    selectedStore,
    selectedYear,
    spendToggleState,
    transformedDataWithDate?.length,
    userData?.data?.is_gale_admin,
  ]);

  // Helper function to check if data is actually empty (all nulls)
  const checkIfDataHasValues = useCallback((data) => {
    if (!data?.length) return false;
    // Check if any row has non-null channel or spend
    return data.some((row) => row.channel !== null && row.spend !== null);
  }, []);

  // Fetch functions
  async function fetchWeeklyActualSpendData() {
    if (!weeklyPayload) return null;
    try {
      setErrorsFunction(setErrors);
      const { component, payload } = weeklyPayload;
      const response = await api.post(COMPONENT_QUERY + `?component=${component}`, payload);
      return response.data;
    } catch (error) {
      setErrors((prev) => ({ ...prev, weekly_data_error: true }));
      console.error(error);
      return null;
    }
  }

  async function fetchColumnHeaders() {
    if (!headersPayload) return null;

    try {
      setErrors((prev) => ({ ...prev, headers_error: false }));
      const { component, payload } = headersPayload;
      const response = await api.post(COMPONENT_QUERY + `?component=${component}`, payload);
      return response.data;
    } catch (error) {
      setErrors((prev) => ({ ...prev, headers_error: true }));
      console.error(error);
      return null;
    }
  }

  async function fetchMonthlyActualSpendData() {
    if (!monthlyPayload) return null;

    try {
      setErrorsFunction(setErrors);
      const { component, payload } = monthlyPayload;
      const response = await api.post(COMPONENT_QUERY + `?component=${component}`, payload);
      return response.data;
    } catch (error) {
      setErrors((prev) => ({ ...prev, monthly_data_error: true }));
      console.error(error);
      return null;
    }
  }

  // Set up mutations
  const {
    data: weeklyActualSpendData,
    mutate: weeklyActualSpendMutation,
    isLoading: weeklyLoading,
  } = useMutation({
    mutationFn: fetchWeeklyActualSpendData,
    onSuccess: () => {
      dataFetchedRef.current.weekly = true;
      viewSwitchTracker.current.weekly.fetched = true;
      // Reset the filters changed flag for weekly since we just fetched fresh data
      filtersChangedSinceLastFetchRef.current.weekly = false;
    },
  });

  const {
    data: weeklyActualSpendTableHeaders,
    mutate: weeklyActualSpendTableHeadersMutation,
    isLoading: headersLoading,
  } = useMutation({
    mutationFn: fetchColumnHeaders,
    onSuccess: () => {
      dataFetchedRef.current.headers = true;
    },
  });

  const {
    data: monthlyActualSpendData,
    mutate: monthlyActualSpendDataMutation,
    isLoading: monthlyLoading,
  } = useMutation({
    mutationFn: fetchMonthlyActualSpendData,
    onSuccess: () => {
      dataFetchedRef.current.monthly = true;
      viewSwitchTracker.current.monthly.fetched = true;
      // Reset the filters changed flag for monthly since we just fetched fresh data
      filtersChangedSinceLastFetchRef.current.monthly = false;
    },
  });

  // Handler functions - stabilized
  const handleYearChange = useCallback((year) => {
    if (year) setSelectedYear(year);
  }, []);

  const getDataForStoreId = useCallback((storeId) => {
    setSelectedStore(storeId);
  }, []);

  // Channel filtering handler
  const changeSelectedFilters = useCallback((value) => {
    if (!value?.length) {
      setShowNoChannelSelected(true);
      setSelectedChannels([]);
      return;
    } else {
      setShowNoChannelSelected(false);
    }

    setSelectedChannels(value);
  }, []);

  // Updated handler for view change - check if data exists before clearing
  const handleActualSpendView = useCallback(
    (event, newView) => {
      if (newView && newView !== actualSpendView) {
        // Store previous view before updating
        previousViewRef.current = actualSpendView;

        // Set the new view state
        setActualSpendView(newView);

        // If switching to Monthly view
        if (newView === 'Monthly') {
          // Check if we need to fetch monthly data
          // Either it hasn't been fetched yet, or filters changed since last fetch
          if (
            !viewSwitchTracker.current.monthly.fetched ||
            filtersChangedSinceLastFetchRef.current.monthly
          ) {
            // Only show loading and clear data if we're going to make a new API call
            setInitialLoading(true);
            setMonthlyTableHeaders([]);
            setMonthlyRowData([]);
            setHasActualData(true);

            // Fetch monthly data
            monthlyActualSpendDataMutation();
          } else {
            // No API call needed, just check if there's actual data
            setHasActualData(monthlyRowData.length > 0 && monthlyTableHeaders.length > 0);
          }
        }

        // If switching to Weekly view
        else if (newView === 'Weekly') {
          // Check if we need to fetch weekly data
          // Either it hasn't been fetched yet, or filters changed since last fetch
          if (
            !viewSwitchTracker.current.weekly.fetched ||
            filtersChangedSinceLastFetchRef.current.weekly
          ) {
            // Only show loading and clear data if we're going to make a new API call
            setInitialLoading(true);
            setWeeklyTableHeaders([]);
            setWeeklyRowData([]);
            setHasActualData(true);

            // Reset weekly data tracking flags
            dataFetchedRef.current.weekly = false;

            // Fetch weekly data for current filters
            weeklyActualSpendMutation();
            weeklyActualSpendTableHeadersMutation();
          } else {
            // No API call needed, just check if there's actual data
            setHasActualData(weeklyRowData.length > 0 && weeklyTableHeaders.length > 0);

            // Turn off loading immediately since we're using cached data
            setInitialLoading(false);
          }
        }
      }
    },
    [
      actualSpendView,
      monthlyActualSpendDataMutation,
      weeklyActualSpendMutation,
      weeklyActualSpendTableHeadersMutation,
      weeklyRowData.length,
      weeklyTableHeaders.length,
      monthlyRowData.length,
      monthlyTableHeaders.length,
    ]
  );

  const getDownloadFilename = useCallback(() => {
    if (actualSpendView === 'Weekly') {
      return spendToggleState
        ? ACTUAL_PLAN_DOWNLOAD_FILE_WEEKLY_LOCAL_SPEND
        : ACTUAL_PLAN_DOWNLOAD_FILE_WEEKLY;
    } else {
      return spendToggleState
        ? ACTUAL_PLAN_DOWNLOAD_FILE_MONTHLY_LOCAL_SPEND
        : ACTUAL_PLAN_DOWNLOAD_FILE_MONTHLY;
    }
  }, [actualSpendView, spendToggleState]);

  // Process weekly data when available - stabilized and fixed
  const processWeeklyData = useCallback(() => {
    if (!weeklyActualSpendData || !weeklyActualSpendTableHeaders || !selectedStore) return;

    const key = spendToggleState
      ? ACTUAL_PLAN_WEEKLY_SPEND_WITHOUT_LOCAL
      : ACTUAL_PLAN_WEEKLY_SPEND;
    const headersKey = spendToggleState
      ? ACTUAL_PLAN_WEEKLY_HEADERS_WITHOUT_LOCAL
      : ACTUAL_PLAN_WEEKLY_HEADERS;

    // Check if we have actual result data
    if (weeklyActualSpendData?.data?.[key]?.result) {
      const resultData = weeklyActualSpendData.data[key].result;

      // Check if data only contains null values for channel and spend
      const hasRealData = checkIfDataHasValues(resultData);
      setHasActualData(hasRealData);

      if (!hasRealData) {
        // If all data is null, show no data
        setWeeklyRowData([]);
        setWeeklyTableHeaders([]);
        return;
      }

      // Process headers if we have real data
      if (weeklyActualSpendTableHeaders?.data?.[headersKey]?.result?.length) {
        const headerData = transformHeaders(
          weeklyActualSpendTableHeaders.data,
          selectedStore,
          headersKey
        );
        const updatedHeaders = getChannelAndAudienceHeaders(spendToggleState);
        const combinedHeaders = [...updatedHeaders, ...headerData];
        setWeeklyTableHeaders(combinedHeaders);

        // Process row data
        const rowData = transformData(weeklyActualSpendData.data, selectedStore, key);
        const updatedData = verifyRowData(rowData, rowData);
        setWeeklyRowData(updatedData);
      } else {
        setWeeklyTableHeaders([]);
        setWeeklyRowData([]);
      }
    } else {
      setHasActualData(false);
      setWeeklyTableHeaders([]);
      setWeeklyRowData([]);
    }
  }, [
    // Change to simpler dependencies to prevent infinite loops
    stableObject(weeklyActualSpendData?.data),
    stableObject(weeklyActualSpendTableHeaders?.data),
    selectedStore,
    spendToggleState,
    checkIfDataHasValues,
  ]);

  // Process monthly data when available - stabilized and fixed
  const processMonthlyData = useCallback(() => {
    if (!monthlyActualSpendData || !selectedStore) return;

    const key = spendToggleState
      ? ACTUAL_PLAN_MONTHLY_SPEND_WITHOUT_LOCAL
      : ACTUAL_PLAN_MONTHLY_SPEND;

    if (monthlyActualSpendData?.data?.[key]?.result) {
      const resultData = monthlyActualSpendData.data[key].result;

      // Check if data only contains null values
      const hasRealData = checkIfDataHasValues(resultData);
      setHasActualData(hasRealData);

      if (!hasRealData) {
        // If all data is null, show no data
        setMonthlyRowData([]);
        setMonthlyTableHeaders([]);
        return;
      }

      const { formattedRowData, tableHeaders } = transformMonthlyData(
        monthlyActualSpendData.data,
        selectedStore,
        key
      );
      const updatedRow = verifyRowData(formattedRowData, tableHeaders, selectedStore);
      const updatedHeaders = getChannelAndAudienceHeaders(spendToggleState);

      setMonthlyTableHeaders([...updatedHeaders, ...tableHeaders]);
      setMonthlyRowData(updatedRow);
    } else {
      setHasActualData(false);
      setMonthlyTableHeaders([]);
      setMonthlyRowData([]);
    }
  }, [
    // Change to simpler dependencies to prevent infinite loops
    stableObject(monthlyActualSpendData?.data),
    selectedStore,
    spendToggleState,
    checkIfDataHasValues,
  ]);

  // Update filtered data when necessary data changes
  useEffect(() => {
    if (!selectedChannels?.length) return;

    const updateModifiedRowData = (originalData, value) => {
      if (!originalData?.length) return [];

      if (value.length === updatedChannelListForActualSpend?.length) {
        setNoDataForSelectedChannel(false);
        return originalData;
      }

      const filteredData = originalData?.filter((row) => value?.includes(row?.channel));
      const hasNoData = filteredData?.length === 0;
      setNoDataForSelectedChannel(hasNoData);

      if (hasNoData) return [];

      const grandTotalColumnData = formateGrandTotalColumnsData(filteredData);
      return [...filteredData, grandTotalColumnData];
    };

    if (actualSpendView === 'Weekly') {
      setModifiedRowData(updateModifiedRowData(weeklyRowData, selectedChannels));
    } else {
      setModifiedMonthlyRowData(updateModifiedRowData(monthlyRowData, selectedChannels));
    }
  }, [
    weeklyRowData,
    monthlyRowData,
    selectedChannels,
    actualSpendView,
    updatedChannelListForActualSpend,
  ]);

  // Initial data loading and filter changes - streamlined to avoid circular loading
  useEffect(() => {
    // Skip if data isn't ready
    if (!transformedDataWithDate?.length || !selectedStore) return;

    const shouldFetchData =
      isFirstMount.current ||
      selectedStore !== filterRef.current.store ||
      selectedYear !== filterRef.current.year ||
      spendToggleState !== filterRef.current.spendToggle;

    // Check if filters have changed
    if (shouldFetchData && !isFirstMount.current) {
      // Mark both views as needing data refresh
      filtersChangedSinceLastFetchRef.current = {
        weekly: true,
        monthly: true,
      };
    }

    // Save current filter state for comparison
    filterRef.current = {
      store: selectedStore,
      year: selectedYear,
      spendToggle: spendToggleState,
    };

    if (shouldFetchData) {
      // Set initial loading state to true when starting to fetch data
      setInitialLoading(true);
      dataFetchInitiatedRef.current = true;

      // Reset data loaded status
      if (isFirstMount.current) {
        isFirstMount.current = false;
      }

      // Clear existing data based on current view to prevent stale UI
      if (actualSpendView === 'Weekly') {
        setWeeklyTableHeaders([]);
        setWeeklyRowData([]);
        // Reset weekly view fetch status
        dataFetchedRef.current.weekly = false;
        viewSwitchTracker.current.weekly.fetched = false;
      } else {
        setMonthlyTableHeaders([]);
        setMonthlyRowData([]);
        // Reset monthly view fetch status
        dataFetchedRef.current.monthly = false;
        viewSwitchTracker.current.monthly.fetched = false;
      }

      setHasActualData(true);

      // Always need fresh headers when filters change
      dataFetchedRef.current.headers = false;

      // Only fetch data based on current view
      if (actualSpendView === 'Weekly') {
        weeklyActualSpendMutation();
        weeklyActualSpendTableHeadersMutation();
      } else {
        // For Monthly view, only call headers and monthly data
        weeklyActualSpendTableHeadersMutation();
        monthlyActualSpendDataMutation();
      }
    }
  }, [
    // Simplify dependency array to avoid re-renders
    transformedDataWithDate?.length,
    selectedStore,
    selectedYear,
    spendToggleState,
    weeklyActualSpendMutation,
    weeklyActualSpendTableHeadersMutation,
    monthlyActualSpendDataMutation,
    actualSpendView,
  ]);

  // Process Weekly Data when available - only when specific data changes
  useEffect(() => {
    if (weeklyActualSpendData && weeklyActualSpendTableHeaders && selectedStore) {
      processWeeklyData();
    }
  }, [
    stableObject(weeklyActualSpendData?.data),
    stableObject(weeklyActualSpendTableHeaders?.data),
    selectedStore,
    processWeeklyData,
  ]);

  // Process Monthly Data when available - only when specific data changes
  useEffect(() => {
    if (monthlyActualSpendData && selectedStore) {
      processMonthlyData();
    }
  }, [stableObject(monthlyActualSpendData?.data), selectedStore, processMonthlyData]);

  // Check if there is data for selected branch
  useEffect(() => {
    // Determine which row data to use based on view and modified data availability
    const getRowData = () => {
      if (actualSpendView === 'Weekly') {
        return modifiedRowData.length ? modifiedRowData : weeklyRowData;
      } else {
        return modifiedMonthlyRowData.length ? modifiedMonthlyRowData : monthlyRowData;
      }
    };
    const currentRows = getRowData();
    const currentCols = actualSpendView === 'Weekly' ? weeklyTableHeaders : monthlyTableHeaders;
    const hasData = currentRows?.length > 0 && currentCols?.length >= 4;
    setNoDataForSelectedBranch(!hasData);
  }, [
    actualSpendView,
    modifiedRowData,
    modifiedMonthlyRowData,
    weeklyRowData,
    monthlyRowData,
    weeklyTableHeaders,
    monthlyTableHeaders,
  ]);

  // Track loading state and turn off initialLoading when data is ready or there's an error
  useEffect(() => {
    // Only check loading states after data fetch has been initiated
    if (!dataFetchInitiatedRef.current) return;

    if (actualSpendView === 'Weekly') {
      // For Weekly view - both weekly data and headers are needed
      if (
        (!weeklyLoading &&
          !headersLoading &&
          dataFetchedRef.current.weekly &&
          dataFetchedRef.current.headers) ||
        errors.weekly_data_error ||
        errors.headers_error
      ) {
        setInitialLoading(false);
      }
    } else if (
      // For Monthly view - only monthly data is needed
      (!monthlyLoading && dataFetchedRef.current.monthly) ||
      errors.monthly_data_error
    ) {
      setInitialLoading(false);
    }
  }, [
    actualSpendView,
    weeklyLoading,
    headersLoading,
    monthlyLoading,
    dataFetchedRef.current.weekly,
    dataFetchedRef.current.headers,
    dataFetchedRef.current.monthly,
    errors.weekly_data_error,
    errors.headers_error,
    errors.monthly_data_error,
  ]);

  // Determine if download button should be disabled
  const isDownloadDisabled = useMemo(
    () =>
      initialLoading ||
      weeklyLoading ||
      headersLoading ||
      monthlyLoading ||
      selectedChannels?.length === 0 ||
      noDataForSelectedChannel ||
      errors.weekly_data_error ||
      errors.headers_error ||
      errors.monthly_data_error ||
      showNoChannelSelected ||
      noDataForSelectedBranch ||
      !hasActualData,
    [
      initialLoading,
      weeklyLoading,
      headersLoading,
      monthlyLoading,
      selectedChannels?.length,
      noDataForSelectedChannel,
      errors.weekly_data_error,
      errors.headers_error,
      errors.monthly_data_error,
      showNoChannelSelected,
      noDataForSelectedBranch,
      hasActualData,
    ]
  );

  // Build download props inside the hook
  const getDownloadProps = useCallback(
    (userData, transformedDataWithDate) => {
      // Check if all channels are selected
      const areAllChannelsSelected =
        selectedChannels?.length === updatedChannelListForActualSpend?.length &&
        selectedChannels?.every((channel) =>
          updatedChannelListForActualSpend.map((c) => c.value).includes(channel)
        );

      // Get base payload
      const basePayload = getPayload(
        userData?.data?.is_gale_admin,
        actualSpendDownloadComponent(actualSpendView === 'Weekly', spendToggleState),
        selectedStore,
        selectedYear,
        true,
        transformedDataWithDate,
        previousWeekExtractor
      );

      // Apply filters conditionally
      const finalPayload = areAllChannelsSelected
        ? basePayload
        : appendChannelFilter(basePayload, selectedChannels, updatedChannelListForActualSpend);

      return {
        component: actualSpendDownloadComponent(actualSpendView === 'Weekly', spendToggleState),
        appliedFilters: finalPayload,
        filename: getDownloadFilename(),
        setIsDownloading: setIsReportDownloading,
        isDownloading: isReportDownloading,
        disabled: isDownloadDisabled,
        customButtonStyle: { marginTop: '8px' },
      };
    },
    [
      actualSpendView,
      spendToggleState,
      selectedStore,
      selectedYear,
      selectedChannels,
      updatedChannelListForActualSpend,
      getDownloadFilename,
      isReportDownloading,
      isDownloadDisabled,
    ]
  );

  return {
    // Data state
    weeklyTableHeaders,
    weeklyRowData,
    monthlyTableHeaders,
    monthlyRowData,
    errors,
    hasActualData,
    // Filter state
    selectedStore,
    selectedYear,
    spendToggleState,
    setSpendToggleState,
    showSpendToggle,
    resetDropdown,
    isReportDownloading,
    setIsReportDownloading,
    actualSpendView,
    // Filter data
    updatedChannelListForActualSpend,
    options,
    yearsList,
    // Loading states
    weeklyLoading,
    headersLoading,
    monthlyLoading,
    initialLoading,
    // Channel selection state and logic
    selectedChannels,
    modifiedRowData,
    modifiedMonthlyRowData,
    noDataForSelectedChannel,
    showNoChannelSelected,
    noDataForSelectedBranch,
    // Handler functions
    handleYearChange,
    getDataForStoreId,
    handleActualSpendView,
    getDownloadFilename,
    changeSelectedFilters,
    // Download props and related
    isDownloadDisabled,
    getDownloadProps,
  };
};

export default useActualSpendData;
